/**
 * ScorerConfigEditor — Admin UI for the AI Lead Targeting scorer.
 *
 * Two parts:
 *   1. **Live scorer overrides** — edit the highest-value tunable knobs
 *      (`ehr_difficulty_penalty`, `apcm_eligible_specialties`, and a
 *      handful of high-impact `specialty_base` rows) without redeploying.
 *      Changes are persisted to `scorer_config` (singleton doc) and
 *      applied to subsequent score calculations immediately.
 *
 *   2. **AI Cohort sync** — manually trigger a refresh of the 12
 *      C-01…C-12 cohort campaign rosters. Useful after editing weights
 *      so the recomputed cohorts land in the campaign target segments.
 *
 * Backed by:
 *   GET  /api/admin/scorer-config
 *   PUT  /api/admin/scorer-config
 *   POST /api/admin/ai-cohorts/sync
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sliders, RefreshCw, Loader2, Save, ShieldCheck, Sparkles, Plus, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Specialty rows we expose for editing — the rest stay at matrix defaults.
const EDITABLE_SPECIALTIES = [
  "Family Medicine", "Internal Medicine", "Geriatric Medicine",
  "Cardiology", "Pulmonology", "Endocrinology", "Nephrology",
  "Behavioral Health",
];
const PRODUCTS = ["EHR", "PM", "RCM", "APCM", "CCM", "RPM", "AWV"];

export default function ScorerConfigEditor() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [draft, setDraft] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/scorer-config`, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(res.data);
      setDraft(JSON.parse(JSON.stringify(res.data)));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load scorer config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading || !draft) {
    return (
      <div data-testid="scorer-config-loading" style={{ padding: 24, color: "#6B7280", fontSize: 13 }}>
        <Loader2 className="animate-spin" style={{ width: 13, height: 13, display: "inline", marginRight: 6 }} />
        Loading scorer config…
      </div>
    );
  }

  const onSpecialtyChange = (spec, product, val) => {
    const next = JSON.parse(JSON.stringify(draft));
    next.specialty_base = next.specialty_base || {};
    next.specialty_base[spec] = next.specialty_base[spec] || {};
    next.specialty_base[spec][product] = Math.max(0, Math.min(100, parseInt(val, 10) || 0));
    setDraft(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      // Send only the values being tuned — backend treats omitted keys
      // as "leave at default".
      const payload = {
        specialty_base:            draft.specialty_base,
        apcm_eligible_specialties: draft.apcm_eligible_specialties,
        ehr_difficulty_penalty:    draft.ehr_difficulty_penalty,
        taxonomy_fit_overrides:    draft.taxonomy_fit_overrides || {},
      };
      const res = await axios.put(`${API}/admin/scorer-config`, payload, { headers: { Authorization: `Bearer ${token}` } });
      setConfig(res.data);
      toast.success("Scorer config updated · live for new scores");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save scorer config");
    } finally {
      setSaving(false);
    }
  };

  const syncCohorts = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/ai-cohorts/sync`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSyncResult(res.data);
      toast.success("AI cohort campaigns refreshed");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cohort sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="scorer-config-editor" style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* ── Header ─────────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: "linear-gradient(135deg,#EEF2FF,#F4EAFE)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Sliders style={{ width: 16, height: 16, color: "#5B6CFF" }} />
        </div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontSize: 16, fontWeight: 650, margin: 0 }}>AI Lead Targeting · Weights</h2>
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>
            Tune the scorer without a code deploy. Changes apply to new score calculations immediately.
          </div>
        </div>
        <div data-testid="scorer-config-source" style={{
          padding: "3px 9px", borderRadius: 999, fontSize: 10, fontWeight: 600,
          background: config?.source === "db" ? "#ECFDF5" : "#F3F4F6",
          color:      config?.source === "db" ? "#065F46" : "#6B7280",
          border:     `0.5px solid ${config?.source === "db" ? "#A7F3D0" : "#E5E7EB"}`,
          display: "inline-flex", alignItems: "center", gap: 4,
        }}>
          <ShieldCheck style={{ width: 10, height: 10 }} /> Source: {config?.source}
          {config?.updated_by ? ` · ${config.updated_by}` : ""}
        </div>
      </div>

      {/* ── EHR difficulty penalty ─────────────────────────────── */}
      <div className="card" style={{ padding: 14, borderRadius: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>EHR Difficulty Penalty</div>
        <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 10 }}>
          Subtracted from every EHR FitScore. Matrix default is 10. Higher = harder to surface EHR as the primary product.
        </div>
        <input
          type="number" min={0} max={50}
          data-testid="ehr-difficulty-input"
          value={draft.ehr_difficulty_penalty}
          onChange={(e) => setDraft({ ...draft, ehr_difficulty_penalty: parseInt(e.target.value, 10) || 0 })}
          style={{ width: 80, padding: "5px 8px", border: "0.5px solid #D1D5DB", borderRadius: 6, fontSize: 12, fontFamily: "ui-monospace, monospace" }}
        />
      </div>

      {/* ── Specialty base scores ──────────────────────────────── */}
      <div className="card" style={{ padding: 14, borderRadius: 10 }}>
        <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Specialty × Product Base Scores</div>
        <div style={{ fontSize: 10, color: "#6B7280", marginBottom: 10 }}>
          ClinicalFit base score (0–100) per specialty per product. Higher = stronger clinical fit signal.
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: "0.5px solid #E5E7EB" }}>
                <th style={{ textAlign: "left", padding: "5px 6px", fontWeight: 600, color: "#6B7280" }}>Specialty</th>
                {PRODUCTS.map(p => (
                  <th key={p} style={{ padding: "5px 6px", fontWeight: 600, color: "#6B7280", textAlign: "center" }}>{p}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {EDITABLE_SPECIALTIES.map(spec => {
                const row = draft.specialty_base?.[spec] || {};
                return (
                  <tr key={spec} style={{ borderBottom: "0.5px solid #F3F4F6" }}>
                    <td style={{ padding: "6px 6px", fontWeight: 500, color: "#111827" }}>{spec}</td>
                    {PRODUCTS.map(p => (
                      <td key={p} style={{ padding: "4px 4px", textAlign: "center" }}>
                        <input
                          type="number" min={0} max={100}
                          data-testid={`spec-${spec.replace(/\s+/g, "-")}-${p}`}
                          value={row[p] ?? 0}
                          onChange={(e) => onSpecialtyChange(spec, p, e.target.value)}
                          style={{
                            width: 52, padding: "3px 5px", border: "0.5px solid #D1D5DB",
                            borderRadius: 5, fontSize: 11, textAlign: "center",
                            fontFamily: "ui-monospace, monospace",
                          }}
                        />
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Iter 168.c (Danny / Addendum 1): Taxonomy-code overrides ─────── */}
      <TaxonomyOverridesEditor
        overrides={draft.taxonomy_fit_overrides || {}}
        products={PRODUCTS}
        onChange={(next) => setDraft({ ...draft, taxonomy_fit_overrides: next })}
      />

      {/* ── Save bar ───────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 6, borderTop: "0.5px solid #E5E7EB" }}>
        <span style={{ fontSize: 10, color: "#6B7280" }}>
          {config?.updated_at ? `Last update: ${new Date(config.updated_at).toLocaleString()}` : "Currently on matrix defaults."}
        </span>
        <button
          onClick={save}
          disabled={saving}
          data-testid="scorer-config-save"
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            padding: "6px 14px", fontSize: 11, fontWeight: 600,
            background: saving ? "#93C5FD" : "#5B6CFF", color: "#fff",
            border: "none", borderRadius: 6, cursor: saving ? "wait" : "pointer",
          }}
        >
          {saving ? <Loader2 className="animate-spin" style={{ width: 11, height: 11 }} /> : <Save style={{ width: 11, height: 11 }} />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      {/* ── Cohort sync ────────────────────────────────────────── */}
      <div className="card" style={{ padding: 14, borderRadius: 10, background: "linear-gradient(135deg,#F0F9FF,#EFF6FF)", border: "0.5px solid #BFDBFE" }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
          <Sparkles style={{ width: 14, height: 14, color: "#2563EB", marginTop: 2 }} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#1E40AF" }}>AI Cohort Sync</div>
            <div style={{ fontSize: 11, color: "#1E3A8A", marginTop: 2 }}>
              Refresh enrollment for the 12 <code style={{ fontSize: 10 }}>C-01…C-12</code> AI-cohort campaigns based on the latest scoring. Closed-stage leads are excluded automatically.
            </div>
          </div>
          <button
            onClick={syncCohorts}
            disabled={syncing}
            data-testid="cohort-sync-btn"
            style={{
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "6px 12px", fontSize: 11, fontWeight: 600,
              background: syncing ? "#93C5FD" : "#2563EB", color: "#fff",
              border: "none", borderRadius: 6, cursor: syncing ? "wait" : "pointer",
            }}
          >
            {syncing ? <Loader2 className="animate-spin" style={{ width: 11, height: 11 }} /> : <RefreshCw style={{ width: 11, height: 11 }} />}
            {syncing ? "Syncing…" : "Sync now"}
          </button>
        </div>

        {syncResult && (
          <div data-testid="cohort-sync-result" style={{ marginTop: 12, background: "#fff", borderRadius: 8, padding: 10, border: "0.5px solid #DBEAFE" }}>
            <table style={{ width: "100%", fontSize: 11, borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "0.5px solid #E5E7EB" }}>
                  <th style={{ textAlign: "left", padding: "4px 6px", fontWeight: 600, color: "#6B7280" }}>Cohort</th>
                  <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600, color: "#6B7280" }}>+ Added</th>
                  <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600, color: "#6B7280" }}>− Removed</th>
                  <th style={{ textAlign: "right", padding: "4px 6px", fontWeight: 600, color: "#6B7280" }}>Total now</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(syncResult.deltas || {}).sort().map(([code, d]) => (
                  <tr key={code} style={{ borderBottom: "0.5px solid #F3F4F6" }}>
                    <td style={{ padding: "4px 6px", fontWeight: 600, fontFamily: "ui-monospace, monospace", color: "#111827" }}>{code}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", color: d.added > 0 ? "#16A34A" : "#9CA3AF", fontFamily: "ui-monospace, monospace" }}>{d.added > 0 ? `+${d.added}` : "0"}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", color: d.removed > 0 ? "#DC2626" : "#9CA3AF", fontFamily: "ui-monospace, monospace" }}>{d.removed > 0 ? `-${d.removed}` : "0"}</td>
                    <td style={{ padding: "4px 6px", textAlign: "right", color: "#111827", fontFamily: "ui-monospace, monospace", fontWeight: 600 }}>{d.now.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * TaxonomyOverridesEditor — Iter 168.c (Danny / Addendum 1).
 *
 * Lets Super Admins override product-fit base scores by NPPES
 * taxonomy code. When a lead's taxonomy code matches a row here,
 * those scores supersede the specialty_base lookup.
 *
 * UI: list of taxonomy rows; each row has a code input + 7 product
 * score inputs + a delete button. "+ Add taxonomy override" appends
 * a blank row. Saves with the rest of the scorer config.
 *
 * Codes use the NPPES taxonomy code book — e.g.
 *   207R00000X = Internal Medicine
 *   207Q00000X = Family Medicine
 *   207RC0000X = Cardiovascular Disease
 *   208D00000X = General Practice
 *
 * Example useful overrides:
 *   - Cardiology (207RC0000X): EHR=70, CCM=92, RPM=98
 *   - Family Medicine (207Q00000X): EHR=85, APCM=90, AWV=85
 */
function TaxonomyOverridesEditor({ overrides, products, onChange }) {
  const entries = Object.entries(overrides || {});

  const addRow = () => {
    const empty = products.reduce((acc, p) => ({ ...acc, [p]: 0 }), {});
    onChange({ ...overrides, "": empty });
  };

  const updateCode = (oldCode, newCode) => {
    if (newCode === oldCode) return;
    const next = { ...overrides };
    next[newCode] = next[oldCode];
    delete next[oldCode];
    onChange(next);
  };

  const updateScore = (code, product, val) => {
    const next = { ...overrides };
    next[code] = { ...(next[code] || {}), [product]: Math.max(0, Math.min(100, parseInt(val, 10) || 0)) };
    onChange(next);
  };

  const removeRow = (code) => {
    const next = { ...overrides };
    delete next[code];
    onChange(next);
  };

  return (
    <div data-testid="taxonomy-overrides-editor" className="card" style={{ padding: 16, borderRadius: 10, border: "0.5px solid #E5E7EB" }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 10, gap: 10 }}>
        <div>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 700, color: "#111827" }}>
            <ShieldCheck style={{ width: 13, height: 13, color: "#059669" }} />
            Taxonomy-code overrides
          </div>
          <div style={{ fontSize: 11, color: "#6B7280", marginTop: 3 }}>
            Per-NPPES-taxonomy fit-score overrides. Trumps specialty_base when set. Use precise codes like <code style={{ fontFamily: "ui-monospace, monospace" }}>207R00000X</code> (Internal Medicine).
          </div>
        </div>
        <button
          onClick={addRow}
          data-testid="taxonomy-override-add"
          style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", fontSize: 11, color: "#1E40AF", background: "#EFF6FF", border: "0.5px solid #BFDBFE", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
          <Plus style={{ width: 11, height: 11 }} /> Add taxonomy override
        </button>
      </div>

      {entries.length === 0 ? (
        <div style={{ padding: "12px 10px", textAlign: "center", fontSize: 11, color: "#9CA3AF", fontStyle: "italic", background: "#F9FAFB", border: "0.5px dashed #E5E7EB", borderRadius: 6 }}>
          No taxonomy overrides yet. Click <strong>Add taxonomy override</strong> to tune scores by NPPES taxonomy code.
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ borderBottom: "0.5px solid #E5E7EB" }}>
                <th style={{ textAlign: "left", padding: "5px 6px", fontWeight: 600, color: "#6B7280", minWidth: 130 }}>Taxonomy code</th>
                {products.map(p => (
                  <th key={p} style={{ padding: "5px 6px", fontWeight: 600, color: "#6B7280", textAlign: "center" }}>{p}</th>
                ))}
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {entries.map(([code, scores], rowIdx) => (
                <tr key={`${code}-${rowIdx}`} style={{ borderBottom: "0.5px solid #F3F4F6" }}>
                  <td style={{ padding: "4px 4px" }}>
                    <input
                      type="text"
                      defaultValue={code}
                      placeholder="e.g. 207R00000X"
                      data-testid={`taxonomy-override-code-${rowIdx}`}
                      onBlur={(e) => updateCode(code, e.target.value.trim().toUpperCase())}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                      style={{ width: "100%", padding: "4px 6px", border: "0.5px solid #D1D5DB", borderRadius: 5, fontSize: 11, fontFamily: "ui-monospace, monospace", color: "#111827", textTransform: "uppercase" }} />
                  </td>
                  {products.map(p => (
                    <td key={p} style={{ padding: "4px 4px", textAlign: "center" }}>
                      <input
                        type="number" min={0} max={100}
                        data-testid={`taxonomy-override-${rowIdx}-${p}`}
                        value={scores[p] ?? 0}
                        onChange={(e) => updateScore(code, p, e.target.value)}
                        style={{ width: 50, padding: "3px 5px", border: "0.5px solid #D1D5DB", borderRadius: 5, fontSize: 11, textAlign: "center", fontFamily: "ui-monospace, monospace" }} />
                    </td>
                  ))}
                  <td style={{ padding: "4px 4px", textAlign: "center" }}>
                    <button
                      data-testid={`taxonomy-override-remove-${rowIdx}`}
                      onClick={() => removeRow(code)}
                      style={{ background: "transparent", border: "none", padding: 2, cursor: "pointer", color: "#9CA3AF" }}
                      aria-label="Remove taxonomy override">
                      <Trash2 style={{ width: 12, height: 12 }} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
